import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, CircularProgress, Alert,
  Chip, Breadcrumbs, Link, Tooltip, LinearProgress, Button, TableSortLabel
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend
} from "recharts";
import { exportXLSX } from "./exportXLSX";

const API = process.env.REACT_APP_API_URL || "";

// Colores semáforo
const getEfectividadColor = (val, min, max) => {
  if (val == null || max === min) return "#9e9e9e";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#2e7d32"; // verde oscuro
  if (ratio >= 0.5) return "#689f38";  // verde claro
  if (ratio >= 0.25) return "#f9a825"; // amarillo
  return "#c62828"; // rojo
};

const getEfectividadBg = (val, min, max) => {
  if (val == null || max === min) return "#f5f5f5";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#e8f5e9";
  if (ratio >= 0.5) return "#f1f8e9";
  if (ratio >= 0.25) return "#fffde7";
  return "#ffebee";
};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : "0%";

function ReportesAfiliaciones({ token }) {
  const [view, setView] = useState("organizaciones"); // "organizaciones" | "referentes"
  const [orgData, setOrgData] = useState([]);
  const [refData, setRefData] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("afiliaciones");
  const [sortDir, setSortDir] = useState("desc");
  const [highlightUid, setHighlightUid] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrganizaciones = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/afiliaciones/por-organizacion`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setOrgData(json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchReferentes = useCallback(async (org, nombre_org) => {
    setLoading(true);
    setError("");
    try {
      let url = `${API}/api/afiliaciones/por-referente?organizacion=${encodeURIComponent(org)}`;
      if (nombre_org) url += `&nombre_organizacion=${encodeURIComponent(nombre_org)}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setRefData(json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOrganizaciones(); }, [fetchOrganizaciones]);

  const handleOrgClick = (org) => {
    setSelectedOrg(org);
    setView("referentes");
    setSearch("");
    fetchReferentes(org.organizacion, org.nombre_organizacion);
  };

  const handleBack = () => {
    setView("organizaciones");
    setSelectedOrg(null);
    setRefData([]);
    setSearch("");
  };

  // Estadísticas rápidas
  const totalAfiliaciones = orgData.reduce((s, r) => s + Number(r.afiliaciones || 0), 0);
  const totalVerificados = orgData.reduce((s, r) => s + Number(r.verificados || 0), 0);
  const totalReferentes = orgData.reduce((s, r) => s + Number(r.total_referentes || 0), 0);
  const totalImpresas = orgData.reduce((s, r) => s + Number(r.credenciales_impresas || 0), 0);
  const totalEntregadas = orgData.reduce((s, r) => s + Number(r.credenciales_entregadas || 0), 0);
  const efectividadGlobal = totalAfiliaciones > 0 ? ((totalVerificados / totalAfiliaciones) * 100).toFixed(1) : 0;

  // Datos para gráficas (vista organizaciones)
  const totalPorImprimir = orgData.reduce((s, r) => s + Number(r.credenciales_por_imprimir || 0), 0);
  const topOrgs = [...orgData]
    .filter(r => Number(r.afiliaciones) > 0)
    .sort((a, b) => Number(b.afiliaciones) - Number(a.afiliaciones))
    .slice(0, 10)
    .map(r => ({
      nombre: (r.nombre_organizacion || r.organizacion || "").length > 22
        ? (r.nombre_organizacion || r.organizacion).slice(0, 22) + "…"
        : (r.nombre_organizacion || r.organizacion || ""),
      registros: Number(r.afiliaciones || 0),
      verificados: Number(r.verificados || 0),
    }));
  const credencialesData = [
    { name: "Entregadas", value: totalEntregadas, color: "#2e7d32" },
    { name: "Impresas", value: Math.max(totalImpresas - totalEntregadas, 0), color: "#e65100" },
    { name: "Por imprimir", value: totalPorImprimir, color: "#9e9e9e" },
  ].filter(d => d.value > 0);
  const embudoData = [
    { etapa: "Registros", valor: totalAfiliaciones, color: "#1565c0" },
    { etapa: "Verificados", valor: totalVerificados, color: "#6a1b9a" },
    { etapa: "Impresas", valor: totalImpresas, color: "#e65100" },
    { etapa: "Entregadas", valor: totalEntregadas, color: "#2e7d32" },
  ];

  // Estadísticas referentes
  const refTotalAfil = refData.reduce((s, r) => s + Number(r.afiliaciones || 0), 0);
  const refTotalVerif = refData.reduce((s, r) => s + Number(r.verificados || 0), 0);
  const refTotalImpresas = refData.reduce((s, r) => s + Number(r.credenciales_impresas || 0), 0);
  const refTotalEntregadas = refData.reduce((s, r) => s + Number(r.credenciales_entregadas || 0), 0);
  const refEfectividad = refTotalAfil > 0 ? ((refTotalVerif / refTotalAfil) * 100).toFixed(1) : 0;

  // Filtro de búsqueda
  const currentData = view === "organizaciones" ? orgData : refData;
  const filtered = currentData.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    const nombre = (r.nombre_organizacion || r.nombre_usuario || r.uid || "").toLowerCase();
    const org = (r.organizacion || "").toLowerCase();
    return nombre.includes(s) || org.includes(s) || (r.nombre_usuario || "").toLowerCase().includes(s);
  }).sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    const an = Number(av), bn = Number(bv);
    const numeric = !isNaN(an) && !isNaN(bn);
    let cmp = numeric ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const handleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortBy(col); setSortDir("desc"); }
  };

  // Min/Max efectividad para semáforo (solo de datos con afiliaciones > 0)
  const dataConAfil = filtered.filter(r => Number(r.afiliaciones) > 0);
  const efectividades = dataConAfil.map(r => Number(r.efectividad || 0));
  const minEfect = efectividades.length ? Math.min(...efectividades) : 0;
  const maxEfect = efectividades.length ? Math.max(...efectividades) : 100;

  // Top/Bottom referentes
  const topRef = view === "referentes" && dataConAfil.length > 0
    ? [...dataConAfil].sort((a, b) => Number(b.efectividad) - Number(a.efectividad))[0] : null;
  const bottomRef = view === "referentes" && dataConAfil.length > 1
    ? [...dataConAfil].sort((a, b) => Number(a.efectividad) - Number(b.efectividad))[0] : null;

  const fecha = orgData.length > 0 ? orgData[0].fecha_actualizacion : "";

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {view === "referentes" && (
          <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, flexGrow: 1 }}>
          {view === "organizaciones" ? "Afiliaciones por Organización" : `Referentes — ${selectedOrg?.nombre_organizacion || selectedOrg?.organizacion}`}
        </Typography>
        <IconButton onClick={() => view === "organizaciones" ? fetchOrganizaciones() : fetchReferentes(selectedOrg?.organizacion, selectedOrg?.nombre_organizacion)} title="Actualizar">
          <RefreshIcon />
        </IconButton>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => {
            const cols = view === "organizaciones"
              ? [
                  {k:"organizacion",l:"Organización"},{k:"nombre_organizacion",l:"Nombre"},{k:"telefono",l:"Teléfono"},
                  {k:"total_referentes",l:"Usuarios",a:"right"},{k:"total_tabletas",l:"Tabletas",a:"right"},
                  {k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},
                  {k:"rechazados",l:"Rechazados",a:"right"},{k:"error",l:"Error",a:"right"},
                  {k:"efectividad",l:"Efectividad%",a:"right"},{k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},
                  {k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}
                ]
              : [
                  {k:"uid",l:"UID"},{k:"nombre_usuario",l:"Nombre Referente"},{k:"nombre_organizacion",l:"Responsable Org."},
                  {k:"centro_impresion",l:"Centro Impresión"},{k:"afiliaciones",l:"Registros",a:"right"},
                  {k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},
                  {k:"error",l:"Error",a:"right"},{k:"efectividad",l:"Efectividad%",a:"right"},
                  {k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},{k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}
                ];
            exportXLSX(filtered, cols, `afiliaciones_${view}_${new Date().toISOString().slice(0,10)}`);
          }}
          sx={{ color: "#217346" }}
        >
          Excel
        </Button>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color={view === "organizaciones" ? "#AF272F" : "inherit"} sx={{ cursor: "pointer", fontWeight: view === "organizaciones" ? 700 : 400 }} onClick={handleBack}>
          Organizaciones
        </Link>
        {view === "referentes" && (
          <Typography color="#AF272F" fontWeight={700}>{selectedOrg?.organizacion}</Typography>
        )}
      </Breadcrumbs>

      {/* KPIs - Solo en vista organizaciones */}
      {view === "organizaciones" && orgData.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #4a148c" }}>
            <AssessmentIcon sx={{ color: "#4a148c", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(orgData.length)}</Typography>
            <Typography variant="caption" color="text.secondary">Organizaciones</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #AF272F" }}>
            <GroupsIcon sx={{ color: "#AF272F", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(totalReferentes)}</Typography>
            <Typography variant="caption" color="text.secondary">Usuarios</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #1565c0" }}>
            <TrendingUpIcon sx={{ color: "#1565c0", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(totalAfiliaciones)}</Typography>
            <Typography variant="caption" color="text.secondary">Registros</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #6a1b9a" }}>
            <TrendingUpIcon sx={{ color: "#6a1b9a", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(totalVerificados)}</Typography>
            <Typography variant="caption" color="text.secondary">Verificados</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #2e7d32" }}>
            <EmojiEventsIcon sx={{ color: "#2e7d32", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{efectividadGlobal}%</Typography>
            <Typography variant="caption" color="text.secondary">Efectividad Global</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #e65100" }}>
            <TrendingUpIcon sx={{ color: "#e65100", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(totalImpresas)}</Typography>
            <Typography variant="caption" color="text.secondary">Cred. Impresas</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: "center", borderTop: "3px solid #00695c" }}>
            <TrendingUpIcon sx={{ color: "#00695c", fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>{num(totalEntregadas)}</Typography>
            <Typography variant="caption" color="text.secondary">Cred. Entregadas</Typography>
          </Paper>
        </Box>
      )}

      {/* Gráficas - Solo en vista organizaciones */}
      {view === "organizaciones" && orgData.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {/* Top 10 organizaciones por registros */}
          <Paper sx={{ p: 2, flex: "2 1 480px", minWidth: 340 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#AF272F", mb: 1 }}>
              Top 10 organizaciones por registros
            </Typography>
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topOrgs} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 10 }} />
                  <RTooltip formatter={(v) => num(v)} />
                  <Legend />
                  <Bar dataKey="registros" name="Registros" fill="#1565c0" radius={[0, 4, 4, 0]} maxBarSize={18} />
                  <Bar dataKey="verificados" name="Verificados" fill="#6a1b9a" radius={[0, 4, 4, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Dona de credenciales */}
          <Paper sx={{ p: 2, flex: "1 1 280px", minWidth: 260 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#AF272F", mb: 1 }}>
              Estado de credenciales
            </Typography>
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={credencialesData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90} paddingAngle={2} label={(e) => num(e.value)}>
                    {credencialesData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <RTooltip formatter={(v) => num(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Embudo de conversión */}
          <Paper sx={{ p: 2, flex: "1 1 320px", minWidth: 280 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#AF272F", mb: 1 }}>
              Embudo de conversión
            </Typography>
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={embudoData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="etapa" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v) => num(v)} />
                  <Bar dataKey="valor" name="Personas" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    {embudoData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* KPIs - Vista referentes */}
      {view === "referentes" && refData.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 130, textAlign: "center", borderTop: "3px solid #1565c0" }}>
            <Typography variant="h6" fontWeight={700}>{num(refTotalAfil)}</Typography>
            <Typography variant="caption" color="text.secondary">Registros</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 130, textAlign: "center", borderTop: "3px solid #6a1b9a" }}>
            <Typography variant="h6" fontWeight={700}>{num(refTotalVerif)}</Typography>
            <Typography variant="caption" color="text.secondary">Verificados</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 130, textAlign: "center", borderTop: "3px solid #2e7d32" }}>
            <Typography variant="h6" fontWeight={700}>{refEfectividad}%</Typography>
            <Typography variant="caption" color="text.secondary">Efectividad</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 130, textAlign: "center", borderTop: "3px solid #e65100" }}>
            <Typography variant="h6" fontWeight={700}>{num(refTotalImpresas)}</Typography>
            <Typography variant="caption" color="text.secondary">Cred. Impresas</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 130, textAlign: "center", borderTop: "3px solid #00695c" }}>
            <Typography variant="h6" fontWeight={700}>{num(refTotalEntregadas)}</Typography>
            <Typography variant="caption" color="text.secondary">Cred. Entregadas</Typography>
          </Paper>
        </Box>
      )}

      {/* Top/Bottom en vista referentes */}
      {view === "referentes" && topRef && bottomRef && topRef.uid !== bottomRef.uid && (
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Paper onClick={() => { setHighlightUid(topRef.uid); setTimeout(() => { const el = document.getElementById(`row-${topRef.uid}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #2e7d32", cursor: "pointer", "&:hover": { bgcolor: "#e8f5e9" } }} title="Click para ir al registro">
            <EmojiEventsIcon sx={{ color: "#2e7d32" }} />
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: "#2e7d32" }}>Mejor: {topRef.nombre_usuario || topRef.uid}</Typography>
              <Typography variant="caption">{pct(topRef.efectividad)} efectividad ({num(topRef.afiliaciones)} afil.)</Typography>
            </Box>
          </Paper>
          <Paper onClick={() => { setHighlightUid(bottomRef.uid); setTimeout(() => { const el = document.getElementById(`row-${bottomRef.uid}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #c62828", cursor: "pointer", "&:hover": { bgcolor: "#ffebee" } }} title="Click para ir al registro">
            <EmojiEventsIcon sx={{ color: "#c62828" }} />
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: "#c62828" }}>Menor: {bottomRef.nombre_usuario || bottomRef.uid}</Typography>
              <Typography variant="caption">{pct(bottomRef.efectividad)} efectividad ({num(bottomRef.afiliaciones)} afil.)</Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Búsqueda */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField
          size="small"
          placeholder={view === "organizaciones" ? "Buscar organización..." : "Buscar referente..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 250, md: 300 } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          {filtered.length} registros {fecha && `• Actualización: ${fecha.slice(0, 16)}`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ maxHeight: "65vh", overflowX: "auto", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {view === "organizaciones" ? (
                <>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="organizacion"} direction={sortBy==="organizacion"?sortDir:"asc"} onClick={()=>handleSort("organizacion")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Organización</TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="nombre_organizacion"} direction={sortBy==="nombre_organizacion"?sortDir:"asc"} onClick={()=>handleSort("nombre_organizacion")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Nombre</TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="telefono"} direction={sortBy==="telefono"?sortDir:"asc"} onClick={()=>handleSort("telefono")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Teléfono</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="total_referentes"} direction={sortBy==="total_referentes"?sortDir:"asc"} onClick={()=>handleSort("total_referentes")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Usuarios</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="total_tabletas"} direction={sortBy==="total_tabletas"?sortDir:"asc"} onClick={()=>handleSort("total_tabletas")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Tabletas</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="afiliaciones"} direction={sortBy==="afiliaciones"?sortDir:"asc"} onClick={()=>handleSort("afiliaciones")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Registros</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="verificados"} direction={sortBy==="verificados"?sortDir:"asc"} onClick={()=>handleSort("verificados")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Verificados</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="rechazados"} direction={sortBy==="rechazados"?sortDir:"asc"} onClick={()=>handleSort("rechazados")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Rechazados</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="error"} direction={sortBy==="error"?sortDir:"asc"} onClick={()=>handleSort("error")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Error</TableSortLabel></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff", minWidth: 130 }}><TableSortLabel active={sortBy==="efectividad"} direction={sortBy==="efectividad"?sortDir:"asc"} onClick={()=>handleSort("efectividad")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Efectividad</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="credenciales_impresas"} direction={sortBy==="credenciales_impresas"?sortDir:"asc"} onClick={()=>handleSort("credenciales_impresas")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Cred. Impresas</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="credenciales_entregadas"} direction={sortBy==="credenciales_entregadas"?sortDir:"asc"} onClick={()=>handleSort("credenciales_entregadas")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Cred. Entregadas</TableSortLabel></TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="uid"} direction={sortBy==="uid"?sortDir:"asc"} onClick={()=>handleSort("uid")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Referente (UID)</TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="nombre_usuario"} direction={sortBy==="nombre_usuario"?sortDir:"asc"} onClick={()=>handleSort("nombre_usuario")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Nombre Referente</TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="nombre_organizacion"} direction={sortBy==="nombre_organizacion"?sortDir:"asc"} onClick={()=>handleSort("nombre_organizacion")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Responsable Org.</TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="centro_impresion"} direction={sortBy==="centro_impresion"?sortDir:"asc"} onClick={()=>handleSort("centro_impresion")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Centro Impresión</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="afiliaciones"} direction={sortBy==="afiliaciones"?sortDir:"asc"} onClick={()=>handleSort("afiliaciones")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Registros</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="verificados"} direction={sortBy==="verificados"?sortDir:"asc"} onClick={()=>handleSort("verificados")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Verificados</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="rechazados"} direction={sortBy==="rechazados"?sortDir:"asc"} onClick={()=>handleSort("rechazados")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Rechazados</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="error"} direction={sortBy==="error"?sortDir:"asc"} onClick={()=>handleSort("error")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Error</TableSortLabel></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff", minWidth: 130 }}><TableSortLabel active={sortBy==="efectividad"} direction={sortBy==="efectividad"?sortDir:"asc"} onClick={()=>handleSort("efectividad")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Efectividad</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="credenciales_impresas"} direction={sortBy==="credenciales_impresas"?sortDir:"asc"} onClick={()=>handleSort("credenciales_impresas")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Cred. Impresas</TableSortLabel></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#AF272F", color: "#fff" }}><TableSortLabel active={sortBy==="credenciales_entregadas"} direction={sortBy==="credenciales_entregadas"?sortDir:"asc"} onClick={()=>handleSort("credenciales_entregadas")} sx={{"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"}}>Cred. Entregadas</TableSortLabel></TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const efVal = Number(row.efectividad || 0);
              const color = getEfectividadColor(efVal, minEfect, maxEfect);
              const bg = getEfectividadBg(efVal, minEfect, maxEfect);
              const isTop = efVal === maxEfect && Number(row.afiliaciones) > 0;
              const isBottom = efVal === minEfect && Number(row.afiliaciones) > 0 && minEfect !== maxEfect;

              return view === "organizaciones" ? (
                <TableRow
                  key={row.organizacion + i}
                  hover
                  onClick={() => handleOrgClick(row)}
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" }, transition: "background 0.2s" }}
                >
                  <TableCell sx={{ fontWeight: 600, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={`Click para ver referentes de ${row.organizacion}`}>
                      <span>{row.organizacion}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.nombre_organizacion}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem" }}>{row.telefono || ""}</TableCell>
                  <TableCell align="right">{num(row.total_referentes)}</TableCell>
                  <TableCell align="right">{num(row.total_tabletas)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{num(row.afiliaciones)}</TableCell>
                  <TableCell align="right">{num(row.verificados)}</TableCell>
                  <TableCell align="right">{num(row.rechazados)}</TableCell>
                  <TableCell align="right">{num(row.error)}</TableCell>
                  <TableCell align="center" sx={{ bgcolor: bg }}>
                    <Chip
                      label={pct(row.efectividad)}
                      size="small"
                      icon={isTop ? <EmojiEventsIcon /> : undefined}
                      sx={{
                        bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.75rem",
                        minWidth: 70
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{num(row.credenciales_impresas)}</TableCell>
                  <TableCell align="right">{num(row.credenciales_entregadas)}</TableCell>
                </TableRow>
              ) : (
                <TableRow
                  key={row.uid + i}
                  id={`row-${row.uid}`}
                  sx={{ bgcolor: highlightUid === row.uid ? "#fff9c4" : isTop ? "#e8f5e9" : isBottom ? "#ffebee" : "inherit", transition: "background 0.5s" }}
                >
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{row.uid}</TableCell>
                  <TableCell sx={{ fontWeight: 500, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.nombre_usuario}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                    {row.nombre_organizacion}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>{row.centro_impresion || "SIN CI"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{num(row.afiliaciones)}</TableCell>
                  <TableCell align="right">{num(row.verificados)}</TableCell>
                  <TableCell align="right">{num(row.rechazados)}</TableCell>
                  <TableCell align="right">{num(row.error)}</TableCell>
                  <TableCell align="center" sx={{ bgcolor: bg }}>
                    <Chip
                      label={pct(row.efectividad)}
                      size="small"
                      icon={isTop ? <EmojiEventsIcon /> : undefined}
                      sx={{
                        bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.75rem",
                        minWidth: 70
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{num(row.credenciales_impresas)}</TableCell>
                  <TableCell align="right">{num(row.credenciales_entregadas)}</TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {search ? "Sin resultados para la búsqueda" : "Sin datos"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ReportesAfiliaciones;
